import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { getJobEvents } from "../../../jobs-list/jobs-layout/actions/getJobEvents";

export interface JobEventsResponse {
  items: any[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  currentEventIndex: number;
}

interface JobEventPaginated {
  parentId: number;
  pageSize?: number;
  order?: "ASC" | "DESC" | "RELEVANCE";
  orderBy?: "startDate" | "createdAt" | "updatedAt";
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  billingStatus?: string;
}

export function useJobEventsPaginated({
  parentId,
  pageSize = 1,
  order = "ASC",
  orderBy,
  status,
  startDate,
  endDate,
  billingStatus,
}: JobEventPaginated) {
  const [page, setPage] = useState(1);

  const query = useQuery<JobEventsResponse, Error, JobEventsResponse>({
    queryKey: ["job-events", parentId, page, pageSize, order, orderBy, status, startDate, endDate, billingStatus],
    queryFn: async () => {
      return (await getJobEvents({
        jobId: parentId,
        page,
        pageSize,
        order,
        orderBy,
        status,
        startDate,
        endDate,
        billingStatus,
      })) as JobEventsResponse;
    },
    enabled: Boolean(parentId),
    staleTime: 1000 * 30,
    // Keep previous page totals/items while fetching so downstream UI (e.g. multi-invoice mode) does not see totalItems === 0.
    placeholderData: keepPreviousData,
  });

  const items = query.data?.items ?? [];
  const currentEventIndex = query.data?.currentEventIndex;

  const totalPages = query.data?.totalPages ?? 1;
  const totalItems = query.data?.totalItems ?? 0;

  const canNextPage = page < totalPages;
  const canPrevPage = page > 1;

  const nextPage = useCallback(() => {
    if (canNextPage) setPage((p) => p + 1);
  }, [canNextPage]);

  const prevPage = useCallback(() => {
    if (canPrevPage) setPage((p) => p - 1);
  }, [canPrevPage]);

  const resetPage = useCallback(() => setPage(1), []);

  const goToCurrentJobPage = () => {
    if (currentEventIndex == null || currentEventIndex < 0) return;
    const pageOfCurrent = Math.floor(currentEventIndex / pageSize) + 1;
    setPage(pageOfCurrent);
  };

  return {
    ...query,
    events: items,
    totalItems,
    currentEventIndex,
    totalPages,
    page,
    pageSize,
    setPage,
    nextPage,
    prevPage,
    resetPage,
    canNextPage,
    canPrevPage,
    goToCurrentJobPage,
  };
}

export type JobEventsPaginatedQuery = ReturnType<typeof useJobEventsPaginated>;
